import { Box, Tooltip } from '@mui/material';
import { Info } from 'lucide-react';
import { ReactNode } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { formatAngle } from '~/utils/obliquity';
import { DrawnImage } from '../adapters/types';
import { HAIRLINE, WARNING } from '../chrome';

interface ImageWarningsMarkProps {
    /** The images a tile draws; the mark warns about those that need it. */
    readonly images: ReadonlyArray<DrawnImage>;
}

/** One image's line in a section, as the card shows it and as it is read out. */
interface WarningLine {
    readonly seriesInstanceUid: string;
    readonly pairLabel: string | undefined;
    readonly shown: ReactNode;
    readonly spoken: string;
}

/** One kind of warning: its heading, a line per image it applies to, and what it means. */
interface WarningSection {
    readonly id: string;
    readonly heading: ReactNode;
    readonly lines: ReadonlyArray<WarningLine>;
    readonly note: ReactNode;
}

/** A number a line puts forward: bold, amber, a step larger. */
function WarningFigure({ children }: { children: ReactNode }) {
    return (
        <Box
            component="span"
            sx={{ color: WARNING, fontWeight: 700, fontSize: 14 }}
        >
            {children}
        </Box>
    );
}

/** The images with interpolated slices: how many of how many, and what such a slice holds. */
function interpolatedSlicesSection(
    intl: IntlShape,
    images: ReadonlyArray<DrawnImage>
): WarningSection {
    return {
        id: 'interpolated-slices',
        heading: <FormattedMessage id="image.interpolatedSlices.label" />,
        lines: images.flatMap(
            ({ seriesInstanceUid, pairLabel, interpolatedSlices }) =>
                interpolatedSlices === undefined
                    ? []
                    : [
                          {
                              seriesInstanceUid,
                              pairLabel,
                              shown: (
                                  <FormattedMessage
                                      id="image.interpolatedSlices.value"
                                      values={{
                                          count: (
                                              <WarningFigure>
                                                  {interpolatedSlices.count}
                                              </WarningFigure>
                                          ),
                                          total: interpolatedSlices.total,
                                      }}
                                  />
                              ),
                              spoken: intl.formatMessage(
                                  { id: 'image.interpolatedSlices.tooltip' },
                                  {
                                      count: interpolatedSlices.count,
                                      total: interpolatedSlices.total,
                                  }
                              ),
                          },
                      ]
        ),
        note: (
            <FormattedMessage
                id={
                    images.some(
                        (image) =>
                            image.interpolatedSlices !== undefined &&
                            image.readoutFollowsOtherImage
                    )
                        ? 'image.interpolatedSlices.noteBlended'
                        : 'image.interpolatedSlices.note'
                }
            />
        ),
    };
}

/** The images acquired tilted: how far, and that contouring waits for a reformat. */
function tiltedAcquisitionSection(
    intl: IntlShape,
    images: ReadonlyArray<DrawnImage>
): WarningSection {
    return {
        id: 'tilted-acquisition',
        heading: <FormattedMessage id="image.oblique.label" />,
        lines: images.flatMap(
            ({ seriesInstanceUid, pairLabel, acquisitionTiltDeg }) => {
                if (acquisitionTiltDeg === undefined) {
                    return [];
                }
                const angle = formatAngle(acquisitionTiltDeg);
                return [
                    {
                        seriesInstanceUid,
                        pairLabel,
                        shown: (
                            <FormattedMessage
                                id="image.oblique.value"
                                values={{
                                    angle: (
                                        <WarningFigure>{angle}</WarningFigure>
                                    ),
                                }}
                            />
                        ),
                        spoken: intl.formatMessage(
                            { id: 'image.oblique.tooltip' },
                            { angle }
                        ),
                    },
                ];
            }
        ),
        note: <FormattedMessage id="image.oblique.note" />,
    };
}

/**
 * One amber mark on a tile title for everything the user must know about
 * the images the tile draws before trusting what they see: slices that
 * were interpolated, and slices acquired tilted. Its card holds a section
 * per warning that applies, each with a line per image, headed by the
 * image's pair label when the tile blends two, and what the warning means.
 * Its accessible name is every line, read out. Never an image's name,
 * which is PHI. Draws nothing when no warning applies.
 */
export default function ImageWarningsMark({ images }: ImageWarningsMarkProps) {
    const intl = useIntl();
    const sections = [
        interpolatedSlicesSection(intl, images),
        tiltedAcquisitionSection(intl, images),
    ].filter((section) => section.lines.length > 0);
    if (sections.length === 0) {
        return null;
    }
    const spoken = sections
        .flatMap((section) => section.lines)
        .map(({ pairLabel, spoken: line }) =>
            pairLabel === undefined ? line : `${pairLabel}: ${line}`
        )
        .join('. ');
    return (
        // The card describes the mark; the mark keeps the lines as its name.
        <Tooltip
            describeChild
            placement="bottom-start"
            title={
                <Box
                    data-testid="tile-image-warnings-details"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        py: 0.5,
                        maxWidth: 240,
                    }}
                >
                    {sections.map((section, index) => (
                        <Box
                            key={section.id}
                            data-testid={`tile-image-warning-${section.id}`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                ...(index > 0
                                    ? {
                                          pt: 1.5,
                                          borderTop: '1px solid',
                                          borderColor: HAIRLINE,
                                      }
                                    : {}),
                            }}
                        >
                            <Box
                                sx={{
                                    color: WARNING,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {section.heading}
                            </Box>
                            {section.lines.map((line) => (
                                <Box
                                    key={line.seriesInstanceUid}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.25,
                                    }}
                                >
                                    {line.pairLabel !== undefined ? (
                                        <Box
                                            component="span"
                                            sx={{ fontSize: 11, opacity: 0.7 }}
                                        >
                                            {line.pairLabel}
                                        </Box>
                                    ) : null}
                                    <Box component="span" sx={{ fontSize: 12 }}>
                                        {line.shown}
                                    </Box>
                                </Box>
                            ))}
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 11,
                                    lineHeight: 1.45,
                                    opacity: 0.7,
                                }}
                            >
                                {section.note}
                            </Box>
                        </Box>
                    ))}
                </Box>
            }
        >
            <Box
                component="span"
                role="img"
                aria-label={spoken}
                // Focusable, so the card opens from the keyboard as well.
                tabIndex={0}
                data-testid="tile-image-warnings"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: WARNING,
                }}
            >
                <Info size={12} />
            </Box>
        </Tooltip>
    );
}
